<template>
  <ResumeEditor
    v-if="ready"
    ref="editor"
    class="resume-editor"
    :is-edit-mode="isEditMode"
    :hidden-id-number="hiddenIdNumber"
    :value="resumeInfo"
    :isNewPage="true"
    :isHr="true"
    :stepSetting="resumeEditorSetting"
    :hrMiniResumeSetting="hrMiniResumeSetting"
    @cancel="handleCancel"
    @save="handleSave"
    @save-back="handleSaveAndBack"
  />
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { STEP_TYPE } from '@/constant/candidate'
import { mixedRouterPush } from '@/router/routes'
import ResumeEditor from '@/components/candidate/editor/layout.vue'

export default {
  components: {
    ResumeEditor
  },
  data () {
    const vm = this
    return {
      ready: false,
      readyPromise: [],
      hiddenIdNumber: true,
      resumeInfo: {
        open_question: {
          hr: []
        }
      },
      resumeEditorSetting: {
        showEdit: false,
        hiddenStep: [],
        displayStep: [],
        [STEP_TYPE.UPLOAD_RESUME]: {
          hiddenField: [],
          needUploadResume: false
        },
        [STEP_TYPE.BASE_INFO]: {
          hiddenField: ['onlyTalentPool']
        },
        [STEP_TYPE.WORK_EXPERIENCE]: {},
        [STEP_TYPE.EDUCATION]: {},
        [STEP_TYPE.LANGUAGE]: {},
        [STEP_TYPE.DESCRIPTION]: {
          hiddenField: ['source', 'code']
        }
      },
      hiddenStepData: [],
      hrMiniResumeSetting: {
        isHrMiniResume: false,
        isHrSwitchMiniResume: !vm.$route.meta.isEditMode
      },
      needUploadResume: false
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    candidateId () {
      return this.$route.params.candidateId
    }
  },
  created () {
    this.readyPromise.push(this.isHiddenIdNumberField())
    if (this.isEditMode) {
      this.readyPromise.push(this.getResumeInfo())
    }
    this.$eventBus.$emit('showLoading', true)
    Promise.all(this.readyPromise).then(() => {
      this.initUrlParams()
      this.initResumeSetting()
      this.ready = true
    }).finally(() => {
      this.$eventBus.$emit('showLoading', false)
    })
  },
  mounted () {
  },
  methods: {
    ...mapActions('application', ['hasIdNumberField']),
    ...mapActions('talent', ['getTalentResumeInfo', 'updateTalentResumeInfo', 'updateTalentResumePhoto']),
    async isHiddenIdNumberField () {
      this.hiddenIdNumber = !await this.hasIdNumberField()
    },
    async getResumeInfo () {
      const resp = await this.getTalentResumeInfo({ candidateId: this.candidateId })
      if (resp) {
        this.resumeInfo = resp
      }
    },
    async initResumeSetting () {
      if (this.needUploadResume) {
        this.resumeEditorSetting[STEP_TYPE.UPLOAD_RESUME].needUploadResume = true
      }
    },
    initUrlParams () {
      if (!this.isEditMode) {
        return
      }
      const stepType = STEP_TYPE.BASE_INFO
      if (this.$route.query.step) {
        return
      }
      const query = { ...this.$route.query, step: stepType }
      this.$router.replace({ query })
    },
    async handleCancel () {
      mixedRouterPush(this.$route.query.back || `v-admin/talent-pool/candidate/profile/resume?candidate_id=${this.candidateId}`)
    },
    async handleSave (resumeInfo, autoBack) {
      this.$eventBus.$emit('showLoading', true)
      if (!this.isEditMode) {
        _.forEach(resumeInfo, (val, key) => {
          if (_.includes(this.hiddenStepData, key)) {
            this.$delete(resumeInfo, key)
          }
        })
        _.forEach(resumeInfo.baseInfo, (val, key) => {
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, key)) {
            this.$delete(resumeInfo.baseInfo, key)
          }
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, 'chatTool')) {
            this.$set(resumeInfo.baseInfo, 'chatTool', [])
          }
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, 'socialNetwork')) {
            this.$set(resumeInfo.baseInfo, 'socialNetwork', [])
          }
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, 'homeLocation')) {
            this.$delete(resumeInfo.baseInfo, 'city')
            this.$delete(resumeInfo.baseInfo, 'province')
            this.$delete(resumeInfo.baseInfo, 'country')
          }
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, 'currentLocation')) {
            this.$delete(resumeInfo.baseInfo, 'currentCity')
            this.$delete(resumeInfo.baseInfo, 'currentCountry')
            this.$delete(resumeInfo.baseInfo, 'currentProvince')
          }
          if (_.includes(this.resumeEditorSetting.BASE_INFO.hiddenField, 'expectedSalary')) {
            this.$delete(resumeInfo.baseInfo, 'salary')
            this.$delete(resumeInfo.baseInfo, 'currency')
          }
        })
      }
      try {
        const photoFile = resumeInfo.baseInfo?.photoFile
        resumeInfo = {
          ...resumeInfo,
          candidateId: this.candidateId,
          baseInfo: {
            ...resumeInfo.baseInfo,
            photo: photoFile ? null : resumeInfo.baseInfo.photo,
            photoFile: null
          }
        }
        const resp = await this.updateTalentResumeInfo(resumeInfo)
        if (resp) {
          if (photoFile) {
            await this.updateTalentResumePhoto({ candidateId: this.candidateId, photo: photoFile })
          }
          this.$message.success(this.$t('common.message.saveSuccess'))
          if (autoBack) {
            mixedRouterPush(this.$route.query.back || `v-admin/talent-pool/candidate/profile/resume?candidate_id=${this.candidateId}`)
          }
        }
      } catch (err) {
        if (err.code !== 500) {
          const editorRef = this.$refs.editor
          const targetStep = _.find(editorRef.activeStepConfigList, { stepType: STEP_TYPE.BASE_INFO })
          await editorRef.showStepPage(targetStep)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleSaveAndBack (jobTemplateInfo) {
      return this.handleSave(jobTemplateInfo, true)
    }
  }
}
</script>

<style lang="scss" scoped>
.resume-editor {
  min-width: 1325px;
  overflow: auto;
}
</style>
